import { REQUEST_METHODS } from '@/constants/network'
import baseFetch from '@/network/baseFetch'
import { SearchIntentType } from '@/types/srp'
import { getPartnerId } from '@/utils/cfw'
import { parseQueryString } from '@/utils'
import { isServer } from '@/utils/general'
import { FETCH_DYNAMIC_CONTENT_PATH } from '@/constants/flights'
import { isUserSignedIn } from '@/utils/general/user'

type CouponsPayloadType = {
    dataId: string
    searchId?: string
    searchIntent: SearchIntentType
    sourceType?: 'SEARCH' | 'INTERSTITIAL'
    couponIntents: {
        requestedIds: string[][]
        travelOptions: {
            [x: string]: {
                travelOptionId: string
                fareIds: any
            }
        }[]
        travelOptionIdsToFareIdsMap: {}[]
    }
    ffSC?: boolean
}
export const airportSearch = async (searchValue: string) => {
    try {
        const header = {
            caller: 'UI'
        }
        const response = await baseFetch({
            url: `/places/airports/search?string=${searchValue}`,
            method: REQUEST_METHODS.GET,
            headers: header
        })
        if (response) {
            return response
        }
        return Promise.reject()
    } catch (e) {
        console.error(e)
        return Promise.reject()
    }
}

export const getSearchResult = async (query: string) => {
    try {
        const queryObject = parseQueryString(query)
        
        let partnerId = getPartnerId(queryObject.cfw)
        const header = {
            caller: 'UI',
            'partner-id': partnerId
        }
        const response = await baseFetch({
            url: `/flight/search/v2?${query}`,
            method: REQUEST_METHODS.GET,
            headers: header
        })
        if (response) {
            return response
        }
        return Promise.reject()
    } catch (e) {
        console.error(e)
        return Promise.reject(e)
    }
}

export const airportSearchBluk = async payload => {
    try {
        const header = {
            caller: 'UI'
        }
        const response = await baseFetch({
            url: '/places/airports/search/v3',
            method: REQUEST_METHODS.POST,
            headers: header,
            body: JSON.stringify(payload)
        })
        if (response) {
            return response
        }
        return Promise.reject()
    } catch (e) {
        console.log(e)
        return Promise.reject()
    }
}

export const getRTFlightCouponsData = async (payload: CouponsPayloadType, isCfw) => {
    try {
        let partnerId = getPartnerId(isCfw)
        const header = {
            caller: 'UI',
            'partner-id': partnerId
        }
        const response = await baseFetch({
            url: `/flight/fetch-coupon-discount`,
            method: REQUEST_METHODS.POST,
            headers: header,
            body: JSON.stringify(payload)
        })
        if (response) {
            return response
        }
        return Promise.reject()
    } catch (e) {
        console.error(e)
        return Promise.reject()
    }
}


const WOW_DEDUP_WINDOW_MS = 60_000
type WowInFlightEntry = { promise: Promise<any>; ts: number }
const WOW_GLOBAL_KEY = '__ctWowInFlight__'
const globalAny = globalThis as any
if (!globalAny[WOW_GLOBAL_KEY]) {
    globalAny[WOW_GLOBAL_KEY] = new Map<string, WowInFlightEntry>()
}
const wowInFlight: Map<string, WowInFlightEntry> = globalAny[WOW_GLOBAL_KEY]

const buildWowDedupKey = (payload: Record<string, any>, isCfw: boolean): string => {
    // dataId + selected card ids (J1/J2) is enough to discriminate distinct
    // logical fetches without serialising the whole payload.
    const dataId = payload?.dataId || ''
    const j1 = (payload?.cards?.J1 || [])
        .map((c: any) => c?.cardId || c?.travelOptionId || '')
        .join(',')
    const j2 = (payload?.cards?.J2 || [])
        .map((c: any) => c?.cardId || c?.travelOptionId || '')
        .join(',')
    const sourceType = payload?.sourceType || ''
    return `${dataId}|${sourceType}|cfw=${isCfw}|J1=${j1}|J2=${j2}`
}

const dedupedDynamicContentFetch = (
    payload: Record<string, any>,
    isCfw: boolean
): Promise<any> => {
    const key = buildWowDedupKey(payload, isCfw)
    const existing = wowInFlight.get(key)
    const now = Date.now()
    if (existing && now - existing.ts < WOW_DEDUP_WINDOW_MS) {
        return existing.promise
    }
    const partnerId = getPartnerId(isCfw)
    const headers = {
        caller: 'UI',
        'partner-id': partnerId
    }
    const promise = baseFetch({
        url: FETCH_DYNAMIC_CONTENT_PATH,
        method: REQUEST_METHODS.POST,
        headers,
        body: JSON.stringify(payload)
    })
        .then((response) => {
            if (!response) {
                wowInFlight.delete(key)
            }
            return response
        })
        .catch((e) => {
            console.error('fetch-dynamic-content error:', e)
            wowInFlight.delete(key)
            return null
        })
    wowInFlight.set(key, { promise, ts: now })
    return promise
}

/**
 * Fetches dynamic-content (WOW pricing) for the SRP cards — initial fetch.
 * Mirrors flights-pwa `getWowPricingSrpData` (UIENG-5276 / UIENG-6192).
 *
 * Response shape (relevant fields):
 *  { data: { fares: { [fareId]: { pricing: {...} } }, wowSorting: { [journeyKey]: cardId[] }, highRpsTravelOptionIds: string[] } }
 *
 * NOTE: All callers go through the module-level dedup so concurrent identical
 * payloads collapse to one network request.
 */
export const getWowPricingSrpData = async (
    payload: Record<string, any>,
    isCfw: boolean = false
): Promise<any> => {
    try {
        if (!isUserSignedIn()) {
            return {
                status: 401,
                data: null,
                error: 'User not signed in'
            }
        }
        const response = await dedupedDynamicContentFetch(payload, isCfw)
        return (
            response ?? {
                status: 500,
                data: null,
                error: 'Error fetching dynamic content'
            }
        )
    } catch (e) {
        console.error('getWowPricingSrpData error:', e)
        return {
            status: 500,
            data: null,
            error: (e && (e as Error).message) || 'Error fetching dynamic content'
        }
    }
}

/**
 * Progressive WOW pricing fetcher — used for batches of 10 cards triggered as
 * the user scrolls past the initial 100. Mirrors flights-pwa UIENG-6303
 * `fetchDynamicContent`. Hard gate: short-circuits to `{}` when the user is
 * not signed in. Shares the same in-flight dedup as `getWowPricingSrpData`.
 */
export const fetchDynamicContent = async (
    payload: Record<string, any>,
    isCfw: boolean = false
): Promise<any> => {
    try {
        if (!isUserSignedIn()) {
            return {}
        }
        const response = await dedupedDynamicContentFetch(payload, isCfw)
        return response ?? {}
    } catch (e) {
        console.error('fetchDynamicContent error:', e)
        return {}
    }
}
function getOfferPath(isMetaSource, isCfw) {
    let offerPath = 'srp_itin'; 

    if (isMetaSource) {
        offerPath = 'itin_meta';
    } else if (isCfw) {
        offerPath = 'cfw_srp_itin';
    }

    return offerPath;
}

const getTripType = (action) => {
    if (action.multiCity) return 'OW';
    if (action.arrivalDate) return 'RT';
    
    return 'OW';
};

export const fetchSrpCoupons = async (action: any, isMetaSource = false) => {
    try {
        const { isCfw, baseUrl, trafficSource = '' } = action || {}
        const tripType = getTripType(action)
        const partnerId = getPartnerId(isCfw)

        const sectorType = action?.international ? 'INT' : 'DOM'
        const url = `/coupons/srp?partnerId=${partnerId}&tripType=${tripType}&sectorType=${sectorType}&channel=DESKTOP&trafficSource=${trafficSource}`
        const headers = {
            'api-version': '3.0'
        }

        const response = await baseFetch({
            url,
            method: REQUEST_METHODS.GET,
            headers,
            domain: baseUrl
        })
        return response
    } catch (ex) {
        return []
    }
}

export const createItineraryApi = async (payload: object, requestHeaders: object, isCFW = false) => {
    let partnerId = getPartnerId(isCFW)
    
    const url = `/itin/v7/itinerary/create`
    const headers = {
        'api-version': '3.0',
        'partner-id': partnerId,
        ...requestHeaders
    }
    const response = await baseFetch({
        url,
        method: REQUEST_METHODS.POST,
        headers,
        body: JSON.stringify(payload)
    })
    return response
}

export const getServicesData = async (payload: object) => {
    try {
        const isCfw = !isServer() ? location?.search?.includes('isCfw=true') : false
        const partnerId = getPartnerId(isCfw)
        const url = `/flight/benefits`
        const headers = {
            'partner-id': partnerId
        }
        const response = await baseFetch({
            url,
            method: REQUEST_METHODS.POST,
            body: JSON.stringify(payload),
            headers
        })
        return response
    } catch (e) {
        return {}
    }
}


export async function getDateTabFares(payload: object) {
    try {
        const headers = {
            caller: 'UI'
        }
        const response = await baseFetch({
            url: `/flights/v1/calendar/tab/fares`,
            method: REQUEST_METHODS.POST,
            body: JSON.stringify(payload),
            headers
        })
        return response
    } catch (e) {
        console.error(e)
        return Promise.reject()
    }
}

/**
 * Fetches the fare-family / interstitial payload.
 *
 * Thin API wrapper only — in-flight dedup and cache updates live in the
 * `fetchInterstitialData` thunk (`interstitialCache` slice). Rejects on
 * transport / HTTP failure so the caller can tell "request failed" apart from
 * "backend returned a body with no fares"; the old `return {}` made those two
 * look the same and the modal treated a 500 as a valid empty response.
 */
export const getSrpFareData = async (payload: Record<string, any>, isCfw = false): Promise<any> => {
    try {
        const headers = {
            'partner-id': String(getPartnerId(isCfw))
        }
        return await baseFetch({
            url: `/flight/fetch-interstitial-page`,
            method: REQUEST_METHODS.POST,
            body: JSON.stringify(payload),
            headers
        })
    } catch (e) {
        console.error(e)
        return Promise.reject(e)
    }
}